import { computed, reactive, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength } from '@vuelidate/validators';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';

import {
  GET_PDF_TRANSCRIPT_CONFIG,
  GET_PDF_TRANSCRIPT_PRESETS,
  GET_TRANSCRIPT_FONT_FAMILIES,
  GET_TRANSCRIPT_STUDENT_INFO_FIELDS,
} from '@/graphql/transcript/queries';
import { UPDATE_PDF_TRANSCRIPT_CONFIG } from '@/graphql/transcript/mutations';
import { useFormErrors } from '@/composables/useFormErrors';
import { toUserMessage } from '@/utils/error';
import { pruneDocumentFields } from '@/utils/studentDocumentFields';
import type { PdfTranscriptConfig, PdfTranscriptConfigInput, PdfTranscriptPreset } from '@/types/composition-functions';

export type { PdfTranscriptPreset };

export interface KeyLabelOption {
  key: string;
  label: string;
}

/** Limits mirror PdfTranscriptConfigInput's @rules on the backend. */
export const TEXT_LIMITS = {
  report_title: 100,
  report_subtitle: 100,
  footer_text: 200,
  watermark_text: 50,
} as const;

/** The settings form always holds a value for every setting; only footer_text may be blank. */
export type PdfTranscriptConfigForm =
  Omit<{ [K in keyof PdfTranscriptConfig]-?: NonNullable<PdfTranscriptConfig[K]> }, 'footer_text'>
  & { footer_text: string | null };

export const MAX_SIGNATURE_LABELS = 4;
export const MAX_SIGNATURE_LABEL_LENGTH = 50;

export const DEFAULT_CONFIG: PdfTranscriptConfigForm = {
  preset: 'classic',
  logo_position: 'center',
  logo_height: 70,
  show_school_name: true,
  show_school_address: true,
  show_report_title: true,
  report_title: 'ACADEMIC TRANSCRIPT',
  report_subtitle: 'Office of the Registrar',
  font_family: 'Times New Roman, serif',
  font_size: 11,
  header_font_size: 18,
  table_header_font_size: 10,
  row_padding: 4,
  show_borders: true,
  striped_rows: false,
  alternate_row_color: '#f9f9f9',
  show_student_info: true,
  student_info_fields: [
    'name', 'matric_no', 'programme', 'department',
    'faculty', 'date_of_birth', 'year_of_admission',
    'year_of_graduation', 'student_status',
  ],
  student_info_layout: 'two_column',
  show_cover_letter: true,
  show_cumulative: true,
  show_semester_summary: true,
  show_partial_footnote: false,
  show_footer: true,
  footer_text: null,
  show_estamp: true,
  show_signature_lines: false,
  signature_labels: ['Registrar'],
  page_size: 'A4',
  page_orientation: 'portrait',
  page_margin: 20,
  show_watermark: false,
  watermark_text: 'CONFIDENTIAL',
};

/**
 * Only keys the save mutation accepts are sent, so a new read-only field can't
 * break saving again. When the school's printable fields are known, student
 * fields it can no longer print (a retired or deleted custom field) are
 * dropped — the backend refuses unknown keys, and the admin cannot untick a
 * field the toggle list no longer shows (#574).
 */
export function toConfigInput(config: object, availableFields: readonly KeyLabelOption[] = []): PdfTranscriptConfigInput {
  const source = config as Record<string, unknown>;
  const input: Record<string, unknown> = {};
  for (const key of Object.keys(DEFAULT_CONFIG)) {
    if (key in source) input[key] = source[key];
  }
  if (Array.isArray(input.student_info_fields)) {
    input.student_info_fields = pruneDocumentFields(input.student_info_fields as string[], availableFields);
  }
  // A blank footer means "use the default"; the backend clears it on ''.
  if (typeof input.footer_text === 'string') input.footer_text = input.footer_text.trim();
  return input as PdfTranscriptConfigInput;
}

export function stripTypename<T extends Record<string, unknown>>(value: T): T {
  const copy = { ...value };
  delete (copy as Record<string, unknown>).__typename;
  return copy;
}

function sameConfig(a: object, b: object): boolean {
  return JSON.stringify(toConfigInput(a)) === JSON.stringify(toConfigInput(b));
}

export function usePdfTranscriptConfigForm() {
  const toast = useToast();
  const { handleServerErrors, clearErrors, getErrorMessage: serverError } = useFormErrors();

  const config = reactive<PdfTranscriptConfigForm>({ ...DEFAULT_CONFIG });
  const saved = ref<PdfTranscriptConfigForm>({ ...DEFAULT_CONFIG });
  const presets = ref<PdfTranscriptPreset[]>([]);
  const studentFields = ref<KeyLabelOption[]>([]);
  const fontFamilies = ref<KeyLabelOption[]>([]);
  const isSaving = ref(false);

  const isDirty = computed(() => !sameConfig(config, saved.value));

  const { onResult: onConfigResult, loading: loadingConfig } = useQuery(GET_PDF_TRANSCRIPT_CONFIG);
  onConfigResult((result) => {
    const incoming = result.data?.pdfTranscriptConfig;
    if (!incoming) return;
    Object.assign(config, stripTypename(incoming));
    saved.value = { ...config };
  });

  const { onResult: onPresetsResult } = useQuery(GET_PDF_TRANSCRIPT_PRESETS);
  onPresetsResult((result) => {
    if (result.data?.pdfTranscriptPresets) presets.value = result.data.pdfTranscriptPresets;
  });

  const { onResult: onFieldsResult } = useQuery(GET_TRANSCRIPT_STUDENT_INFO_FIELDS);
  onFieldsResult((result) => {
    if (result.data?.transcriptStudentInfoFields) studentFields.value = result.data.transcriptStudentInfoFields;
  });

  const { onResult: onFontsResult } = useQuery(GET_TRANSCRIPT_FONT_FAMILIES);
  onFontsResult((result) => {
    if (result.data?.transcriptFontFamilies) fontFamilies.value = result.data.transcriptFontFamilies;
  });

  const tooLong = (limit: number) => helpers.withMessage(
    `Keep this to ${limit} characters or fewer.`,
    maxLength(limit),
  );

  const rules = computed(() => ({
    report_title: { maxLength: tooLong(TEXT_LIMITS.report_title) },
    report_subtitle: { maxLength: tooLong(TEXT_LIMITS.report_subtitle) },
    footer_text: { maxLength: tooLong(TEXT_LIMITS.footer_text) },
    watermark_text: { maxLength: tooLong(TEXT_LIMITS.watermark_text) },
    signature_labels: {
      count: helpers.withMessage(
        `You can add up to ${MAX_SIGNATURE_LABELS} signature lines.`,
        (labels: unknown) => !Array.isArray(labels) || labels.length <= MAX_SIGNATURE_LABELS,
      ),
      length: helpers.withMessage(
        `Each signature label must be ${MAX_SIGNATURE_LABEL_LENGTH} characters or fewer.`,
        (labels: unknown) => !Array.isArray(labels)
          || labels.every(l => String(l).length <= MAX_SIGNATURE_LABEL_LENGTH),
      ),
    },
  }));

  const v$ = useVuelidate(rules, config);

  /** Client-side message first, then whatever the server said for this field. */
  function fieldError(field: string): string {
    const clientError = (v$.value as unknown as Record<string, { $errors?: Array<{ $message: unknown }> }>)[field]
      ?.$errors?.[0]?.$message;
    if (clientError) return String(clientError);
    return serverError(field);
  }

  function textLength(field: keyof typeof TEXT_LIMITS): string {
    const value = config[field];
    return `${typeof value === 'string' ? value.length : 0}/${TEXT_LIMITS[field]}`;
  }

  function applyPreset(preset: PdfTranscriptPreset) {
    Object.assign(config, stripTypename(preset.config), { preset: preset.key });
  }

  function isStudentFieldEnabled(key: string): boolean {
    const fields = config.student_info_fields;
    return !fields || fields.includes(key);
  }

  function toggleStudentField(key: string, enabled: boolean) {
    const fields = [...(config.student_info_fields ?? [])];
    config.student_info_fields = enabled
      ? (fields.includes(key) ? fields : [...fields, key])
      : fields.filter(f => f !== key);
  }

  function discardChanges() {
    Object.assign(config, saved.value);
    clearErrors();
    v$.value.$reset();
  }

  const { mutate: updateConfig } = useMutation(UPDATE_PDF_TRANSCRIPT_CONFIG);

  async function saveConfig(): Promise<boolean> {
    clearErrors();
    if (!(await v$.value.$validate())) {
      toast.add({
        severity: 'warn',
        summary: 'Check the highlighted fields',
        detail: 'Some settings need fixing before they can be saved.',
        life: 6000,
      });
      return false;
    }

    isSaving.value = true;
    try {
      const result = await updateConfig({ input: toConfigInput(config, studentFields.value) });
      const updated = result?.data?.updatePdfTranscriptConfig;
      if (updated) Object.assign(config, stripTypename(updated));
      saved.value = { ...config };
      v$.value.$reset();
      toast.add({
        severity: 'success',
        summary: 'Transcript settings saved',
        detail: 'New transcript PDFs will use these settings.',
        life: 4000,
      });
      return true;
    } catch (error) {
      handleServerErrors(error);
      toast.add({
        severity: 'error',
        summary: 'Settings not saved',
        detail: toUserMessage(error, 'We couldn\'t save your transcript settings. Please try again — if it keeps happening, contact support.'),
        life: 8000,
      });
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  return {
    config,
    presets,
    studentFields,
    fontFamilies,
    isSaving,
    isDirty,
    loadingConfig,
    v$,
    fieldError,
    textLength,
    applyPreset,
    isStudentFieldEnabled,
    toggleStudentField,
    discardChanges,
    saveConfig,
  };
}
